import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";
import { formatCurrency } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { ContractFinancialEstimate } from "./financials";

interface DuplicateCalculatedValuesProps {
  source: ContractFinancialEstimate;
  deltas: ContractFinancialEstimate;
  currency: string | null;
  onlyTotal?: boolean;
}

interface RowProps {
  labelKey: string;
  fullLabelKey: string;
  source: number;
  delta: number;
  currency: string | null;
  showChange: boolean;
  emphasized?: boolean;
}

const ValueRow: React.FC<RowProps> = ({
  labelKey,
  fullLabelKey,
  source,
  delta,
  currency,
  showChange,
  emphasized
}) => {
  const { t } = useTranslation();
  const changed = Math.round(delta) !== 0;

  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-center gap-1">
        <Text variant="bodySecondary">{t(labelKey)}</Text>
        <Text variant="bodyTertiary">({t(fullLabelKey)})</Text>
      </div>
      <div className="flex items-center">
        <Text
          variant="bodyTertiary"
          className={cn("w-32 text-right", changed && "line-through")}
        >
          {formatCurrency(source, currency)}
        </Text>
        <Text
          variant={emphasized ? "lgSubtitle" : "body"}
          className={cn("w-32 text-right", emphasized && "font-semibold")}
        >
          {formatCurrency(source + delta, currency)}
        </Text>
        {showChange ? (
          <Text
            variant="body"
            className={cn(
              "w-28 text-right",
              changed && (delta > 0 ? "text-positive" : "text-error")
            )}
          >
            {changed
              ? `${delta > 0 ? "+" : "-"}${formatCurrency(Math.abs(delta), currency)}`
              : ""}
          </Text>
        ) : null}
      </div>
    </div>
  );
};

const DuplicateCalculatedValues: React.FC<DuplicateCalculatedValuesProps> = ({
  source,
  deltas,
  currency,
  onlyTotal
}) => {
  const { t } = useTranslation();
  const showChange = [deltas.mrr, deltas.cmrr, deltas.acv, deltas.tcv].some(
    (delta) => Math.round(delta) !== 0
  );

  return (
    <div
      // No fill, matching the other calculated-values summaries.
      className="flex flex-col w-full -mt-3 pt-6 border border-border-border1 rounded-b-xl px-5 pb-2.5"
      data-test="duplicate-calculated-values"
    >
      <div className="flex items-center justify-between mb-2">
        <Text variant="bodyTertiary">
          {t("contractTerms.calculatedValues")}
        </Text>
        <div className="flex items-center">
          <Text variant="bodyTertiary" className="w-32 text-right">
            {t("contracts.duplicate.calculated.source")}
          </Text>
          <Text variant="bodyTertiary" className="w-32 text-right">
            {t("contracts.duplicate.calculated.new")}
          </Text>
          {showChange ? (
            <Text variant="bodyTertiary" className="w-28 text-right">
              {t("contracts.duplicate.calculated.change")}
            </Text>
          ) : null}
        </div>
      </div>
      {onlyTotal ? null : (
        <>
          <ValueRow
            labelKey="contractTerms.mrr"
            fullLabelKey="contractTerms.mrrFull"
            source={source.mrr}
            delta={deltas.mrr}
            currency={currency}
            showChange={showChange}
          />
          <ValueRow
            labelKey="contractTerms.cmrr"
            fullLabelKey="contractTerms.cmrrFull"
            source={source.cmrr}
            delta={deltas.cmrr}
            currency={currency}
            showChange={showChange}
          />
          <ValueRow
            labelKey="contractTerms.acv"
            fullLabelKey="contractTerms.acvFull"
            source={source.acv}
            delta={deltas.acv}
            currency={currency}
            showChange={showChange}
          />
        </>
      )}
      <ValueRow
        labelKey="contractTerms.tcv"
        fullLabelKey="contractTerms.tcvFull"
        source={source.tcv}
        delta={deltas.tcv}
        currency={currency}
        showChange={showChange}
        emphasized
      />
    </div>
  );
};

export default DuplicateCalculatedValues;
